import { useI18n } from '@/i18n'
import { Banner, Button, ChoiceGroup, Field, Input, PhotoCapture, PickerSheet } from '@/ui'
import type { StorageAnswer } from '../../commands'
import { useCropLabel } from '../../components/labels'
import { ActionBar } from '../../components/GateScreen'
import { nextStep } from '../../model/delivery'
import type { SeasonLight } from '../../model/seasonLight'
import { useWizard } from './context'
import { useSeasonLight } from './hooks'

const ANOTHER = '__another__'

export function SeasonLightDot({ light }: { light: SeasonLight | null }) {
  const { t } = useI18n()
  if (!light) return null
  return (
    <div
      className={`flex items-center gap-2 rounded-card border px-3 py-2 text-[14px] ${light === 'GREEN' ? 'border-ok/30 bg-ok-wash' : 'border-warn/30 bg-warn-wash'}`}
    >
      <span
        aria-hidden="true"
        className={`h-4 w-4 rounded-full ${light === 'GREEN' ? 'bg-ok' : 'bg-warn'}`}
      />
      <span className="font-semibold">
        {t(light === 'GREEN' ? 'gate.season.green' : 'gate.season.amber')}
      </span>
    </div>
  )
}

/** F4.2 (cont.) — crop, field, who brought it, the two home-storage questions. */
export function StepDetails() {
  const { t } = useI18n()
  const ctx = useWizard()
  const { d, dispatch, roster, stamp } = ctx
  const cropLabel = useCropLabel(roster.config)
  const light = useSeasonLight(ctx)
  const farmer = roster.farmers.find((f) => f.user_id === d.farmer.id)
  const cultivations = farmer?.cultivations ?? []
  // His crops this season; with no registered field at all, any crop with a card (as 'Another field').
  const ownCrops = [...new Set(cultivations.map((c) => c.crop_id))]
  const crops =
    ownCrops.length > 0 ? ownCrops : [...new Set(roster.rateCards.map((c) => c.crop_id))]
  const cropId = d.crop_id ?? (crops.length === 1 ? crops[0]! : null)
  const fields = cultivations.filter((c) => c.crop_id === cropId)
  const fieldValue = d.another_field ? ANOTHER : d.cultivation_id
  const other = d.delivered_by?.kind === 'OTHER' ? d.delivered_by : null

  const answers = (field: 'stored_over_7_days' | 'tablet_or_powder') => (
    <ChoiceGroup<StorageAnswer>
      options={[
        { value: 'YES', label: t('gate.answer.YES'), tone: 'warn' },
        { value: 'NO', label: t('gate.answer.NO') },
        { value: 'DONT_KNOW', label: t('gate.answer.DONT_KNOW'), tone: 'warn' },
      ]}
      value={d.storage[field]}
      onChange={(v) => dispatch({ type: 'storage', field, value: v, at: stamp() })}
    />
  )
  const risky = [d.storage.stored_over_7_days, d.storage.tablet_or_powder].some(
    (a) => a === 'YES' || a === 'DONT_KNOW',
  )

  const otherOk =
    !other || (other.name.trim() !== '' && /^\d{10}$/.test(other.phone) && !!other.photo_media_id)
  const ok =
    !!cropId &&
    (d.another_field || !!d.cultivation_id) &&
    !!d.delivered_by &&
    otherOk &&
    d.storage.stored_over_7_days !== null &&
    d.storage.tablet_or_powder !== null

  return (
    <div className="flex flex-col gap-4">
      <Field label={t('gate.field.crop')} required>
        <PickerSheet
          title={t('gate.field.crop')}
          value={cropId}
          onChange={(v) =>
            dispatch({
              type: 'details',
              patch: {
                crop_id: v,
                cultivation_id: null,
                crop_variety_id: null,
                another_field: false,
              },
            })
          }
          options={crops.map((c) => ({ value: c, label: cropLabel(c) }))}
          placeholder={crops.length === 0 ? t('gate.details.noCrops') : undefined}
        />
      </Field>
      {cropId && (
        <Field label={t('gate.field.field')} required>
          <PickerSheet
            title={t('gate.field.field')}
            value={fieldValue}
            onChange={(v) => {
              if (v === ANOTHER)
                dispatch({
                  type: 'details',
                  patch: {
                    crop_id: cropId,
                    another_field: true,
                    cultivation_id: null,
                    crop_variety_id: null,
                  },
                })
              else {
                const c = fields.find((x) => x.id === v)
                dispatch({
                  type: 'details',
                  patch: {
                    crop_id: cropId,
                    another_field: false,
                    cultivation_id: v,
                    crop_variety_id: c?.crop_variety_id ?? null,
                  },
                })
              }
            }}
            options={[
              ...fields.map((c) => ({
                value: c.id,
                label: c.plot_label ?? c.id,
                description: c.area ? t('gate.details.area', { area: String(c.area) }) : undefined,
                meta: c.photo_url ? (
                  <img src={c.photo_url} alt="" className="h-10 w-10 rounded-chip object-cover" />
                ) : undefined,
              })),
              { value: ANOTHER, label: t('gate.details.anotherField') },
            ]}
          />
        </Field>
      )}
      {d.another_field && <Banner tone="warn">{t('gate.details.anotherFieldWarn')}</Banner>}

      <Field label={t('gate.field.broughtBy')} required>
        <ChoiceGroup
          options={[
            { value: 'SELF', label: t('gate.details.himself') },
            { value: 'OTHER', label: t('gate.details.someoneElse') },
          ]}
          value={d.delivered_by?.kind ?? null}
          onChange={(v) =>
            dispatch({
              type: 'details',
              patch: {
                delivered_by:
                  v === 'SELF'
                    ? { kind: 'SELF' }
                    : { kind: 'OTHER', name: '', phone: '', photo_media_id: null },
              },
            })
          }
        />
      </Field>
      {other && (
        <div className="flex flex-col gap-3 rounded-card border border-rule bg-paper-tint p-3">
          <Field label={t('gate.details.delivererName')} required>
            <Input
              size="touch"
              value={other.name}
              onChange={(e) =>
                dispatch({
                  type: 'details',
                  patch: { delivered_by: { ...other, name: e.target.value } },
                })
              }
            />
          </Field>
          <Field
            label={t('gate.details.delivererPhone')}
            required
            error={
              other.phone && !/^\d{10}$/.test(other.phone) ? t('gate.details.tenDigits') : undefined
            }
          >
            <Input
              size="touch"
              inputMode="numeric"
              className="font-mono"
              value={other.phone}
              onChange={(e) =>
                dispatch({
                  type: 'details',
                  patch: {
                    delivered_by: {
                      ...other,
                      phone: e.target.value.replace(/\D/g, '').slice(0, 10),
                    },
                  },
                })
              }
            />
          </Field>
          <Field label={t('gate.details.delivererPhoto')} required>
            <PhotoCapture
              kind="person"
              value={other.photo_media_id}
              onChange={(id) =>
                dispatch({
                  type: 'details',
                  patch: { delivered_by: { ...other, photo_media_id: id } },
                })
              }
            />
          </Field>
        </div>
      )}

      <Field label={t('gate.storage.q1')} required>
        {answers('stored_over_7_days')}
      </Field>
      <Field label={t('gate.storage.q2')} required>
        {answers('tablet_or_powder')}
      </Field>
      {risky && <Banner tone="warn">{t('gate.storage.riskNote')}</Banner>}

      {light && (
        <div>
          <div className="mb-1 text-[13px] font-semibold">{t('gate.season.label')}</div>
          <SeasonLightDot light={light} />
        </div>
      )}

      <ActionBar>
        <Button
          size="xl"
          fullWidth
          disabled={!ok}
          onClick={() => {
            if (cropId && cropId !== d.crop_id)
              dispatch({ type: 'details', patch: { crop_id: cropId } })
            dispatch({ type: 'goto', step: nextStep(d, 'details') })
          }}
        >
          {t('common.next')}
        </Button>
      </ActionBar>
    </div>
  )
}
